import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../../components/Navbar";
import { Footer } from "../../components/Footer";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { 
  Shield, ArrowLeft, ArrowRight, Briefcase, Clock, MapPin, 
  CheckCircle, XCircle, HelpCircle, ExternalLink
} from "lucide-react";

// Schema.org Article structured data
const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Guida ai Permessi Svizzeri: G, L e B per Lavoratori in Ticino",
  "description": "Scopri le differenze tra Permesso G (Frontalieri), L (Breve termine) e B (Residenti). Tabella comparativa e requisiti completi per lavorare in Canton Ticino.",
  "author": {
    "@type": "Organization",
    "name": "LavoroTicino.ch"
  },
  "publisher": {
    "@type": "Organization",
    "name": "LavoroTicino.ch",
    "logo": {
      "@type": "ImageObject",
      "url": "https://lavoroticino.ch/logo.png"
    }
  },
  "datePublished": "2026-01-15",
  "dateModified": "2026-01-15",
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": "https://lavoroticino.ch/guide/permessi-lavoro"
  },
  "keywords": "Permesso G Ticino, Permesso L Svizzera, Permesso B, Frontalieri, Lavoro Ticino"
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Quanto costa il permesso G per frontalieri?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Il permesso G ha un costo di circa CHF 65-85 per il rilascio. Non ci sono tasse annuali di rinnovo, ma il rinnovo stesso costa circa CHF 40-60 a seconda del Cantone."
      }
    },
    {
      "@type": "Question",
      "name": "Posso vivere in Svizzera con il permesso G?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No, il permesso G è riservato ai frontalieri che devono rientrare nel paese di residenza almeno una volta alla settimana. Per risiedere in Svizzera serve un permesso B o L."
      }
    },
    {
      "@type": "Question",
      "name": "Quanto tempo ci vuole per ottenere un permesso di lavoro svizzero?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "I tempi variano: il permesso G richiede circa 2-4 settimane, il permesso L circa 4-6 settimane, mentre il permesso B può richiedere 6-8 settimane o più."
      }
    }
  ]
};

export default function PermessiLavoroGuide() {
  useEffect(() => {
    // Inject structured data
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify([articleSchema, faqSchema]);
    document.head.appendChild(script);
    
    // Scroll to top
    window.scrollTo(0, 0);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Link to="/guide-lavoro" className="hover:text-[#004E64]">Guide</Link>
            <span>/</span>
            <span className="text-slate-900">Permessi di Lavoro</span>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <header className="bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6" />
            </div>
            <span className="text-sm font-medium text-white/80 uppercase tracking-wider">
              Guida Completa
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            Guida ai Permessi Svizzeri:<br />
            G, L e B per Lavoratori in Ticino
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mb-6">
            Scopri quale permesso di lavoro ti serve per lavorare in Canton Ticino. 
            Confronto dettagliato tra Permesso G (Frontalieri), L e B.
          </p>
          <div className="flex items-center gap-4 text-sm text-white/70">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" /> 8 min di lettura
            </span>
            <span>•</span>
            <span>Aggiornato: Gennaio 2026</span>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="py-10 sm:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg prose-slate max-w-none">
            
            {/* Intro */}
            <p className="text-xl text-slate-700 leading-relaxed mb-8">
              Per lavorare legalmente in <strong>Canton Ticino</strong> come cittadino straniero, 
              è necessario ottenere un permesso di lavoro svizzero. I principali tipi di permesso 
              sono il <strong>Permesso G</strong> per frontalieri, il <strong>Permesso L</strong> per 
              soggiorni brevi e il <strong>Permesso B</strong> per residenti.
            </p>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">1</span>
              Permesso G - Frontalieri
            </h2>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-6">
              <h3 className="font-bold text-blue-900 mb-2">Chi sono i Frontalieri?</h3>
              <p className="text-blue-800 m-0">
                I <strong>frontalieri</strong> sono lavoratori stranieri che risiedono in un paese confinante 
                (Italia, Francia, Germania, Austria) e attraversano quotidianamente il confine per lavorare in Svizzera.
              </p>
            </div>

            <p>
              Il <strong>Permesso G Ticino</strong> è il documento più richiesto per chi vuole lavorare in 
              Canton Ticino mantenendo la residenza in Italia. È ideale per chi vive nelle province di 
              Como, Varese o nel Verbano-Cusio-Ossola.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">Requisiti per il Permesso G:</h3>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Contratto di lavoro con un datore di lavoro svizzero</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Residenza nella zona di frontiera (entro 20 km dal confine, regola generale)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Rientro settimanale nel paese di residenza (almeno 1 volta)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Cittadinanza UE/AELS o permesso di soggiorno valido nel paese di residenza</span>
              </li>
            </ul>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-sm font-bold">2</span>
              Permesso L - Soggiorno Breve
            </h2>

            <p>
              Il <strong>Permesso L</strong> è destinato ai lavoratori che intendono risiedere in Svizzera 
              per un periodo limitato, generalmente inferiore a un anno. È spesso utilizzato per:
            </p>

            <ul className="space-y-2 mb-6">
              <li>Contratti a tempo determinato (stagionali, progetti)</li>
              <li>Stage e tirocini aziendali</li>
              <li>Incarichi temporanei per aziende estere</li>
            </ul>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg mb-6">
              <h3 className="font-bold text-amber-900 mb-2">Durata massima</h3>
              <p className="text-amber-800 m-0">
                Il Permesso L può essere rilasciato per un massimo di 12 mesi. È possibile richiederne 
                il rinnovo, ma dopo un certo periodo è consigliabile passare al Permesso B.
              </p>
            </div>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-sm font-bold">3</span>
              Permesso B - Residenza
            </h2>

            <p>
              Il <strong>Permesso B</strong> è il permesso di soggiorno per chi intende stabilirsi in Svizzera 
              a lungo termine. È richiesto un contratto di lavoro a tempo indeterminato o di durata 
              superiore ai 12 mesi.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">Vantaggi del Permesso B:</h3>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Residenza stabile in Svizzera (con famiglia)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Accesso al sistema sanitario svizzero</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Possibilità di richiedere il Permesso C dopo 5-10 anni</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Maggiore stabilità per mutui e affitti</span>
              </li>
            </ul>

            {/* Comparison Table */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-6">
              Tabella Comparativa dei Permessi
            </h2>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="px-4 py-3 text-left font-semibold">Caratteristica</th>
                    <th className="px-4 py-3 text-center font-semibold">Permesso G</th>
                    <th className="px-4 py-3 text-center font-semibold">Permesso L</th>
                    <th className="px-4 py-3 text-center font-semibold">Permesso B</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr className="bg-slate-50">
                    <td className="px-4 py-3 font-medium">Residenza in Svizzera</td>
                    <td className="px-4 py-3 text-center"><XCircle className="w-5 h-5 text-red-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center"><CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center"><CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Durata massima</td>
                    <td className="px-4 py-3 text-center">5 anni (rinnovabile)</td>
                    <td className="px-4 py-3 text-center">12 mesi</td>
                    <td className="px-4 py-3 text-center">5 anni (rinnovabile)</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-3 font-medium">Rinnovo automatico</td>
                    <td className="px-4 py-3 text-center"><CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center"><XCircle className="w-5 h-5 text-red-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center"><CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Ricongiungimento familiare</td>
                    <td className="px-4 py-3 text-center"><XCircle className="w-5 h-5 text-red-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center">Limitato</td>
                    <td className="px-4 py-3 text-center"><CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-3 font-medium">Imposta alla fonte</td>
                    <td className="px-4 py-3 text-center"><CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center"><CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center"><CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Costo orientativo</td>
                    <td className="px-4 py-3 text-center">CHF 65-85</td>
                    <td className="px-4 py-3 text-center">CHF 90-120</td>
                    <td className="px-4 py-3 text-center">CHF 120-150</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* How to Apply */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
              Come richiedere un Permesso di Lavoro
            </h2>

            <p>
              La procedura per ottenere un permesso di lavoro in Canton Ticino è generalmente 
              gestita dal datore di lavoro svizzero. Ecco i passaggi principali:
            </p>

            <ol className="space-y-4 my-6">
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-[#004E64] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                <div>
                  <strong>Offerta di lavoro:</strong> Ricevi un'offerta contrattuale da un'azienda svizzera
                </div>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-[#004E64] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                <div>
                  <strong>Richiesta del datore:</strong> L'azienda presenta domanda all'Ufficio della Migrazione
                </div>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-[#004E64] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                <div>
                  <strong>Documenti:</strong> Fornisci passaporto, foto, contratto, eventuale diploma
                </div>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-[#004E64] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                <div>
                  <strong>Rilascio:</strong> Ricevi il permesso (tempi: 2-8 settimane a seconda del tipo)
                </div>
              </li>
            </ol>
          </div>

          {/* FAQ Section */}
          <div className="mt-12 pt-8 border-t">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-[#004E64]" />
              Domande Frequenti
            </h2>

            <div className="space-y-4">
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">Quanto costa il permesso G per frontalieri?</h3>
                  <p className="text-slate-600">
                    Il permesso G ha un costo di circa <strong>CHF 65-85</strong> per il rilascio. Non ci sono 
                    tasse annuali di rinnovo, ma il rinnovo stesso costa circa CHF 40-60 a seconda del Cantone. 
                    A questi costi potrebbero aggiungersi spese amministrative del datore di lavoro.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">Posso vivere in Svizzera con il permesso G?</h3>
                  <p className="text-slate-600">
                    <strong>No</strong>, il permesso G è riservato ai frontalieri che devono rientrare nel paese 
                    di residenza almeno una volta alla settimana. Per risiedere stabilmente in Svizzera serve 
                    un permesso B o L. Tuttavia, è possibile pernottare occasionalmente per motivi di lavoro.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">Quanto tempo ci vuole per ottenere un permesso di lavoro svizzero?</h3>
                  <p className="text-slate-600">
                    I tempi variano in base al tipo di permesso: il <strong>permesso G</strong> richiede circa 
                    2-4 settimane, il <strong>permesso L</strong> circa 4-6 settimane, mentre il <strong>permesso B</strong> 
                    può richiedere 6-8 settimane o più. I tempi possono allungarsi in periodi di alta richiesta.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* CTA Banner */}
          <div className="mt-12 bg-gradient-to-r from-[#004E64] to-[#006B8A] rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-3">Pronto a candidarti?</h3>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Guarda le ultime offerte di lavoro su LavoroTicino.ch e trova l'opportunità perfetta per te.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/jobs">
                <Button size="lg" className="bg-[#DA291C] hover:bg-[#B91C1C] text-white">
                  <Briefcase className="w-5 h-5 mr-2" />
                  Vedi Offerte di Lavoro
                </Button>
              </Link>
              <Link to="/register?type=candidate">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Iscriviti Gratis
                </Button>
              </Link>
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-10 flex items-center justify-between pt-6 border-t">
            <Link to="/guide-lavoro" className="flex items-center gap-2 text-slate-600 hover:text-[#004E64]">
              <ArrowLeft className="w-4 h-4" /> Torna alle Guide
            </Link>
            <Link to="/guide/cv-svizzero" className="flex items-center gap-2 text-[#004E64] font-medium hover:underline">
              Prossima: CV Svizzero <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
